import { useCallback, useMemo, useState } from 'react';
import { createQuizRevisionConversation } from '../services/quizRevisionService';
import { buildRevisionQuizQuestionData, mapRevisionLearningObjectives } from '../utils/buildRevisionQuizQuestionData';

// Stable defaults: a new [] or {} on every render would rebuild the revisions.
const EMPTY_QUESTIONS = [];
const NO_LEARNING_OBJECTIVES = {};

/**
 * Hook to manage quiz revision conversation creation. It never creates on its own: each revision
 * spends an interaction of the student, so they are only created when createConversations is called.
 * @param {Object} job - Quiz job object with metadata
 * @param {string} userId - External user ID
 * @param {Object} questionsLearningObjectives - Map of questionGuid to learning objectives array
 * @param {Array} frozenQuestions - Questions as the student saw them, to fill in answers stored without their question
 * @returns {Object} { isCreating, conversations, createConversations, revisionCount }: revisionCount is how many revisions createConversations would create
 */
export function useQuizRevisionConversations(job, userId, questionsLearningObjectives = NO_LEARNING_OBJECTIVES, frozenQuestions = EMPTY_QUESTIONS) {
  const [isCreating, setIsCreating] = useState(false);
  const [conversations, setConversations] = useState({});

  // Built before the student asks for them: «See my results» needs to know how many they would spend.
  const revisionPayloads = useMemo(() => {
    if (!job?.metadata || !userId) {
      return [];
    }

    const parseJobDataToArray = (jsonString) => {
      try {
        const parsed = JSON.parse(jsonString);

        if (Array.isArray(parsed)) {
          return parsed;
        }

        if (typeof parsed === 'object' && parsed !== null) {
          return Object.keys(parsed)
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => parsed[key]);
        }

        throw new Error('Unexpected format');
      } catch (error) {
        console.error('Failed to parse:', error.message);
        return [];
      }
    };

    try {
      const metadata = JSON.parse(job.metadata);
      const jobData = parseJobDataToArray(job.data);
      const frozenQuestionList = Array.isArray(frozenQuestions) ? frozenQuestions : [];

      if (!metadata.answers || metadata.answers.length === 0) {
        console.warn('No quiz questions found in metadata');
        return [];
      }

      // metadata.answers only holds the questions the student touched, index-aligned with the questions: a blank
      // one is stored as null, lacks `question` or has no entry at all. Nothing else creates their revisions, so
      // blank questions get theirs from the frozen question, with `answer: null`.
      const answeredGuids = new Set(metadata.answers.map((answer, index) => answer?.questionGuid || jobData[index]?.guid));
      const untouchedEntries = frozenQuestionList
        .map((question) => question?.question_guid || question?.guid)
        .filter((questionGuid) => questionGuid && !answeredGuids.has(questionGuid))
        .map((questionGuid) => ({ questionGuid }));
      const payloads = [...metadata.answers, ...untouchedEntries].map((answer, index) => {
        // TODO: FALLBACK BASED ON INDEX - THIS HAS RELIABILITY ISSUES TO REVIEW.
        // Reason for the fallback is that the metadata in the job has multiple formats (2 identified) and the questionGuid is not available in one of the 2 formats
        // The metadata and data are saved from the front and there are multiple locations where the student can solve activities and the hypothesis is that the way the metadata and data are saved are different.
        const questionGuid = answer?.questionGuid || jobData[index]?.guid;
        if (!questionGuid) {
          console.warn('[useQuizRevisionConversations] Missing questionGuid in answer payload, no revision conversation created. Index:', index);
          return null;
        }
        const frozenQuestion = frozenQuestionList.find((question) => (question?.question_guid || question?.guid) === questionGuid) || frozenQuestionList[index];
        const quizQuestionData = buildRevisionQuizQuestionData({ answerEntry: answer, frozenQuestion, questionGuid });
        if (!quizQuestionData) {
          console.warn('[useQuizRevisionConversations] No question data for the answer, no revision conversation created. Index:', index);
          return null;
        }

        return {
          externalUserId: userId,
          quizJobGuid: job.guid,
          questionReferenceId: questionGuid,
          quizQuestionData,
          learningObjectives: mapRevisionLearningObjectives(questionsLearningObjectives[questionGuid]),
        };
      });

      return payloads.filter(Boolean);
    } catch (error) {
      console.error('[useQuizRevisionConversations] Unreadable job metadata:', error);
      return [];
    }
  }, [job, userId, questionsLearningObjectives, frozenQuestions]);

  const createConversations = useCallback(async () => {
    if (revisionPayloads.length === 0) {
      return;
    }

    setIsCreating(true);

    try {
      // Create all conversations in parallel
      const promises = revisionPayloads.map((payload) =>
        createQuizRevisionConversation(payload)
          .then((response) => {
            return {
              questionReferenceId: payload.questionReferenceId,
              status: 'ready',
              conversationId: response.conversationId,
              error: null,
            };
          })
          .catch((error) => {
            console.error('[useQuizRevisionConversations] Failed for question:', payload.questionReferenceId, error);
            return {
              questionReferenceId: payload.questionReferenceId,
              status: 'failed',
              conversationId: null,
              error: error.message,
            };
          })
      );

      const results = await Promise.all(promises);

      // Build conversations state map
      const conversationsMap = {};
      results.forEach((result) => {
        conversationsMap[result.questionReferenceId] = result;
      });

      setConversations(conversationsMap);
    } catch (error) {
      console.error('Failed to create quiz revision conversations:', error);
    } finally {
      setIsCreating(false);
    }
  }, [revisionPayloads]);

  return {
    isCreating,
    conversations,
    createConversations,
    revisionCount: revisionPayloads.length,
  };
}
